import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import { RiskAssessmentResultControlType as Risk_Assessment_Result_Control_Type_Enum } from '@risksmart-app/domain/src/types/consts/risk-assessment-result-control-type';
import { useTranslation } from 'react-i18next';

import Link from '@/components/link';
import { useConfigIds } from '@/ratings/useConfigIds';
import { useRiskRatingResolver } from '@/ratings/useRiskRatingResolver';
import { useGetTablePropsWithoutUrlHash } from '@/utils/table/hooks/useGetTablePropsWithoutUrlHash';
import type { TableFields, TablePropsWithActions } from '@/utils/table/types';
import { dateColumnFromConfig } from '@/utils/table/utils/dateColumn';
import { oversightAssessmentResultsEditUrl } from '@/utils/urls';

import type {
  OversightResultFields,
  OversightResultPresentationFields,
  OversightResultRegisterFields,
} from './types';
import { useLabelledFields } from './useLabelledFields';

const useGetFieldConfig = (
  configIds: string[]
): TableFields<OversightResultRegisterFields> => {
  const { t } = useTranslation('common', {
    keyPrefix: 'assessmentResults.columns',
  });
  const { getByValue: getByResultValue } = useRating(
    'performance_result',
    'oversight'
  );
  const { getByValue: getEffectivenessByValue } = useRating(
    'effectiveness',
    'oversight'
  );
  const { resolveRiskRating } = useRiskRatingResolver('oversight', {
    configType: 'oversight',
    configIds,
  });
  const ratingFns = {
    document_oversight_result: (d: OversightResultPresentationFields) =>
      getByResultValue(d.Rating),
    obligation_oversight_result: (d: OversightResultPresentationFields) =>
      getByResultValue(d.Rating),
    control_test_oversight_result: (d: OversightResultPresentationFields) =>
      getEffectivenessByValue(d.OverallEffectiveness),
  };

  return {
    TypeLabelled: {
      header: t('Type'),
      cell: (item: OversightResultRegisterFields) => {
        return (
          <Link
            variant={'secondary'}
            href={oversightAssessmentResultsEditUrl(item.ParentId, item.Id)}
          >
            {item.TypeLabelled}
          </Link>
        );
      },
      isRowHeader: true,
    },
    ParentTitle: {
      header: t('Item'),
    },
    RatingLabelled: {
      header: t('Result'),
      cell: (item: OversightResultRegisterFields) => {
        if (
          item.typename === 'risk_controlled_oversight_result' ||
          item.typename === 'risk_uncontrolled_oversight_result'
        ) {
          const resolved = resolveRiskRating({
            likelihood: item.Likelihood,
            impact: item.Impact,
            controlType:
              item.typename === 'risk_controlled_oversight_result'
                ? Risk_Assessment_Result_Control_Type_Enum.Controlled
                : Risk_Assessment_Result_Control_Type_Enum.Uncontrolled,
            rating: item.Rating,
            configId: item.ConfigId,
          });

          return <SimpleRatingBadge rating={resolved ?? { label: '-' }} />;
        }

        return (
          <SimpleRatingBadge
            rating={ratingFns[item.typename as keyof typeof ratingFns](item)}
          />
        );
      },
      sortingField: 'Rating',
    },
    TestDate: dateColumnFromConfig({
      header: { header: t('TestDate') },
      dateField: 'TestDate',
    }),
    Rationale: {
      header: t('Rationale'),
    },
  };
};

export const useGetCollectionStatelessTableProps = (
  records: OversightResultFields[] | undefined
): TablePropsWithActions<OversightResultRegisterFields> => {
  const { t: at } = useTranslation('common', {
    keyPrefix: 'assessmentResults',
  });

  const configIds = useConfigIds(records);

  const labelledFields = useLabelledFields(records, configIds);
  const fields = useGetFieldConfig(configIds);

  return useGetTablePropsWithoutUrlHash({
    customAttributeFormIds: [],
    data: labelledFields,
    entityLabel: at('entity_name'),
    emptyCollectionAction: <></>,
    preferencesStorageKey: 'AssessmentResults-Preferences',
    enableFiltering: true,
    initialColumns: [
      'TestDate',
      'ParentTitle',
      'TypeLabelled',
      'RatingLabelled',
    ],
    fields,
  });
};
